import React, { forwardRef, useImperativeHandle } from 'react';
import FormField from '../../../../components/common/FormField';
import { GLCompletionData } from '../../../../models/CompletionModels';
import { useFormValidation, useFormFieldHandler } from '../../../../utilities/completionsUtils';
import {
  solutionGORCCorrelationSelect,
  formationVolumeFactorCorrelation,
  viscosityCorrelation,
  inflowModelSelect,
} from './CompletionDropdownData';

interface CompletionsGLCompletionsTabProps {
  glCompletion: GLCompletionData;
  setGLCompletion: React.Dispatch<React.SetStateAction<GLCompletionData>>;
  externalValidationErrors?: { [fieldName: string]: string };
}

const CompletionsGLCompletionsTab = forwardRef<any, CompletionsGLCompletionsTabProps>(
  ({ glCompletion, setGLCompletion, externalValidationErrors = {} }, ref) => {
    const validation = useFormValidation({ tabName: 'glCompletion' });
    const handleFieldChange = useFormFieldHandler(setGLCompletion, validation);

    const displayErrors =
      Object.keys(externalValidationErrors).length > 0 ? externalValidationErrors : validation.errors;

    useImperativeHandle(ref, () => ({
      triggerValidation: (data: GLCompletionData) => {
        validation.validateAllFields(data);
      },
    }));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Reservoir pressure'
            name='reservoirPressure'
            type='text'
            value={glCompletion.reservoirPressure}
            required
            onChange={(_, v) => handleFieldChange('reservoirPressure', v as string)}
            error={displayErrors.reservoirPressure}
          />
          <FormField
            label='Reservoir temperature'
            name='reservoirTemperature'
            type='text'
            value={glCompletion.reservoirTemperature}
            onChange={(_, v) => handleFieldChange('reservoirTemperature', v as string)}
            error={displayErrors.reservoirTemperature}
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Gas gravity'
            name='gasGravity'
            type='text'
            value={glCompletion.gasGravity}
            onChange={(_, v) => handleFieldChange('gasGravity', v as string)}
            error={displayErrors.gasGravity}
          />
          <FormField
            label='Gas gravity - Injected'
            name='gasGravityInjected'
            type='text'
            value={glCompletion.gasGravityInjected}
            required
            onChange={(_, v) => handleFieldChange('gasGravityInjected', v as string)}
            error={displayErrors.gasGravityInjected}
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Oil gravity'
            name='oilGravity'
            type='text'
            value={glCompletion.oilGravity}
            required
            onChange={(_, v) => handleFieldChange('oilGravity', v as string)}
            error={displayErrors.oilGravity}
          />
          <div style={{ flex: 1 }} />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Solution gas oil ratio'
            name='solutionGasOilRatio'
            type='select'
            value={glCompletion.solutionGasOilRatio}
            required
            onChange={(_, v) => handleFieldChange('solutionGasOilRatio', v as string)}
            options={solutionGORCCorrelationSelect}
            error={displayErrors.solutionGasOilRatio}
          />
          <FormField
            label='Formation volume factor correlation'
            name='formationVolumeFactorCorrelation'
            type='select'
            value={glCompletion.formationVolumeFactorCorrelation}
            required
            onChange={(_, v) => handleFieldChange('formationVolumeFactorCorrelation', v as string)}
            options={formationVolumeFactorCorrelation}
            error={displayErrors.formationVolumeFactorCorrelation}
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Viscosity correlation'
            name='viscosityCorrelation'
            type='select'
            value={glCompletion.viscosityCorrelation}
            required
            onChange={(_, v) => handleFieldChange('viscosityCorrelation', v as string)}
            options={viscosityCorrelation}
            error={displayErrors.viscosityCorrelation}
          />
          <FormField
            label='Inflow model'
            name='inflowModel'
            type='select'
            value={glCompletion.inflowModel}
            required
            onChange={(_, v) => handleFieldChange('inflowModel', v as string)}
            options={inflowModelSelect}
            error={displayErrors.inflowModel}
          />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='OHTC (BTU/hrft2f)'
            name='ohtc'
            type='text'
            value={glCompletion.ohtc}
            onChange={(_, v) => handleFieldChange('ohtc', v as string)}
            error={displayErrors.ohtc}
          />
          <FormField
            label='Gauge depth'
            name='gaugeDepth'
            type='text'
            value={glCompletion.gaugeDepth}
            onChange={(_, v) => handleFieldChange('gaugeDepth', v as string)}
            error={displayErrors.gaugeDepth}
          />
        </div>
      </div>
    );
  },
);

export default CompletionsGLCompletionsTab;
